import React, { useCallback } from 'react'

import { useLazyQuery, useMutation } from '@apollo/client'
import { createId } from '@paralleldrive/cuid2'
import { IconTrash } from '@tabler/icons-react'
import { message, Table } from 'antd'
import Form, { Rule } from 'antd/lib/form'
import Image from 'next/image'
import Link from 'next/link'

import { WorkerQrCodeSignatureCaptureBlockValues } from '../../templateEditor/BlockConfigs/WorkerQrCodeSignatureCaptureBlockConfig'
import {
  CompetentPersonSignatureItem,
  WorkerQrCodeSignatureCaptureBlock as WorkerQrCodeSignatureCaptureBlockType,
  WorkerQrCodeSignatureItem,
} from '../../types/blueprint'

import { CaptureBlockFormItem } from './CaptureBlockFormItem'

import { SignatureAttachmentUploader } from '@/components/form-components/SignatureAttachmentUploader'
import { Button } from '@/components/shared-components/BUI/Button'
import { Text } from '@/components/shared-components/BUI/Text'
import { Flexbox } from '@/components/shared-components/Flexbox'
import { useConfirm } from '@/context/confirmContext'
import { useLocale } from '@/context/localeContext'
import { useTemplateRenderer } from '@/context/templateRendererContext'
import { TimezonedDate } from '@/context/timezoneContext'
import { useUser } from '@/context/userContext'
import { useWorkerWebContext } from '@/context/WorkerWebContext'
import { useWebInduction } from '@/hooks/useWebInduction'
import { GET_SITE_INDUCTION_BY_WORKER_PUBLIC } from '@/modules/inductions/graphQL'
import {
  CREATE_WORKER_SIGN_ON_FROM_FORM_BY_WORKER,
  CREATE_WORKER_SIGN_ON_FROM_FORM,
} from '@/modules/signOns/graphQL'
import AddManualSignatureForm from '@/modules/workers/QRCodes/AddManualSignatureForm'
import ScanWorkerQrCodes from '@/modules/workers/QRCodes/ScanWorkerQrCodes'
import { routes } from '@/utils/routes'

import {
  CreateWorkerSignOnFromFormWorkerUiMutation,
  CreateWorkerSignOnFromFormWorkerUiMutationVariables,
  CreateWorkerSignOnFromFormUiMutation,
  CreateWorkerSignOnFromFormUiMutationVariables,
  SiteInductionByWorkerPublicUiQuery,
  SiteInductionByWorkerPublicUiQueryVariables,
  Resource,
} from 'types/api.graphql'

const SignInSheetOptions = ({
  projectId,
  value,
  onChange,
  handleSignOn,
}: {
  projectId: string
  value?: WorkerQrCodeSignatureItem[]
  onChange?: (value: WorkerQrCodeSignatureItem[]) => void
  handleSignOn: (workerId: string) => Promise<void>
}) => {
  const { showConfirm } = useConfirm()

  const [getWorkerDetails] = useLazyQuery<
    SiteInductionByWorkerPublicUiQuery,
    SiteInductionByWorkerPublicUiQueryVariables
  >(GET_SITE_INDUCTION_BY_WORKER_PUBLIC, {
    fetchPolicy: 'network-only',
  })
  const handleNewScans = async (newScans: WorkerQrCodeSignatureItem[]) => {
    const dedupedNewScans =
      newScans.filter(
        (scan) =>
          !value?.some(
            (worker) =>
              worker.workerId === scan.workerId ||
              (worker.firstName === scan.firstName &&
                worker.lastName === scan.lastName)
          )
      ) ?? []

    await Promise.all(
      dedupedNewScans.map((scan) => handleSignOn(scan.workerId))
    )

    onChange([...(value ?? []), ...dedupedNewScans])
  }

  const handleAddManualSignature = useCallback(
    async (values: { workerId: string; key: string; signatureUrl: string }) => {
      if (!values?.workerId || !values?.signatureUrl || !values?.key) {
        return false
      }

      const currentWorkers = [...(value ?? [])]

      if (
        currentWorkers.some((worker) => worker.workerId === values.workerId)
      ) {
        message.warning('Worker already signed')
        return false
      }

      const workerDetails = await getWorkerDetails({
        variables: {
          workerId: values.workerId,
          projectId,
        },
      })

      if (!workerDetails?.data?.projectInductionByWorker_public?.workerId) {
        message.warning(
          'Worker has not yet been inducted. Please have them induct to the project before continuing.'
        )
        return
      }

      await handleSignOn(
        workerDetails.data.projectInductionByWorker_public.workerId
      )

      const workerSignatureItem: WorkerQrCodeSignatureItem = {
        firstName:
          workerDetails.data?.projectInductionByWorker_public?.firstName,
        lastName: workerDetails.data?.projectInductionByWorker_public?.lastName,
        signedViaQrCode: false,
        createdAt: new Date().toISOString(),
        signatureUrl: values.signatureUrl,
        ...values,
      }

      currentWorkers.push(workerSignatureItem)
      onChange(currentWorkers)

      message.success('Worker signature added')

      return true
    },
    [getWorkerDetails, handleSignOn, onChange, projectId, value]
  )

  const handleDeleteWorker = useCallback(
    async (workerToDelete: WorkerQrCodeSignatureItem) => {
      const shouldDelete = await showConfirm({
        title: 'Delete worker signature',
        message: `Are you sure you want to delete the signature for ${workerToDelete.firstName} ${workerToDelete.lastName}?`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        isDestructive: true,
      })

      if (shouldDelete) {
        const workerToDeleteIndex = value?.findIndex(
          (worker) =>
            worker.workerId === workerToDelete.workerId ||
            (worker.firstName === workerToDelete.firstName &&
              worker.lastName === workerToDelete.lastName)
        )

        if (workerToDeleteIndex !== -1) {
          const newWorkers = [...(value ?? [])]
          newWorkers.splice(workerToDeleteIndex, 1)
          onChange(newWorkers)
          message.success('Worker signature deleted')
        }
      }
    },
    [onChange, showConfirm, value]
  )

  return (
    <>
      <Flexbox
        flexDirection="column"
        gap="1rem"
        alignItems="center"
        style={{ width: '100%' }}
        mb="2rem"
      >
        <ScanWorkerQrCodes
          onScanListChange={handleNewScans}
          scannedCodes={value ?? []}
        />
        <Flexbox flexDirection="column" alignItems="center">
          <Text strong>or</Text>
        </Flexbox>
        <AddManualSignatureForm
          title={'Record worker signature'}
          onSubmit={handleAddManualSignature}
          projectId={projectId}
        />
      </Flexbox>

      {value?.length > 0 && (
        <Flexbox flexDirection="column" gap="1rem">
          <Text variant="label02">Scanned workers ({value?.length}):</Text>
          <Table
            pagination={false}
            columns={[
              {
                title: 'Name',
                dataIndex: 'firstName',
                render: (_text, record) => (
                  <Text>
                    {record.firstName} {record.lastName}
                  </Text>
                ),
              },
              {
                title: 'Signed at',
                dataIndex: 'createdAt',
                render: (text, record) => (
                  <TimezonedDate date={record.createdAt} />
                ),
              },
              {
                title: 'Signature',
                dataIndex: 'signatureUrl',
                render: (text, record) =>
                  record.signedViaQrCode ? (
                    <Text>Signed via QR code</Text>
                  ) : record.signatureUrl ? (
                    <Image
                      src={record.signatureUrl}
                      alt="Signature"
                      fill={true}
                    />
                  ) : (
                    <Text>No signature available</Text>
                  ),
              },
              {
                title: '',
                dataIndex: 'actions',
                render: (_, record) => (
                  <Flexbox
                    flexDirection="row"
                    gap="0rem"
                    style={{ width: '64px' }}
                  >
                    <Button
                      negative
                      variant="secondary-text"
                      icon={<IconTrash size={16} />}
                      onClick={() => handleDeleteWorker(record)}
                    />
                  </Flexbox>
                ),
              },
            ]}
            dataSource={value}
          />
        </Flexbox>
      )}
    </>
  )
}

const CompetentPersonsSignature = ({
  projectId,
  value,
  onChange,
}: {
  projectId: string
  value?: CompetentPersonSignatureItem
  onChange?: (value: CompetentPersonSignatureItem) => void
}) => {
  return (
    <>
      <Text variant="label02">Competent person&apos;s signature:</Text>
      <SignatureAttachmentUploader
        projectId={projectId}
        resource={Resource.TemplateResponse}
        onChange={onChange}
        value={value}
      />
    </>
  )
}

const SignInSheet = ({
  onChange,
  value,
  projectId,
  templateName,
  id,
}: {
  onChange?: (value: WorkerQrCodeSignatureCaptureBlockValues) => void
  value?: WorkerQrCodeSignatureCaptureBlockValues
  projectId: string
  templateName: string
  id: string
}) => {
  const { t } = useLocale()
  const { user: adminUser } = useUser() ?? {}
  const { project: workerWebProject } = useWorkerWebContext() ?? {}

  const { userWithWorker } = useWebInduction()

  const [createWorkerSignOnFromFormByWorker] = useMutation<
    CreateWorkerSignOnFromFormWorkerUiMutation,
    CreateWorkerSignOnFromFormWorkerUiMutationVariables
  >(CREATE_WORKER_SIGN_ON_FROM_FORM_BY_WORKER)
  const [createWorkerSignOnFromForm] = useMutation<
    CreateWorkerSignOnFromFormUiMutation,
    CreateWorkerSignOnFromFormUiMutationVariables
  >(CREATE_WORKER_SIGN_ON_FROM_FORM)

  // Helper to sign on a worker or competent person
  const handleSignOn = useCallback(
    async (workerId: string) => {
      if (workerWebProject?.publicProject?.id) {
        await createWorkerSignOnFromFormByWorker({
          variables: {
            input: {
              projectId: workerWebProject.publicProject.id,
              idempotencyKey: createId(),
              workerId: workerId,
              formName: templateName,
            },
          },
        })
      } else if (adminUser?.id) {
        await createWorkerSignOnFromForm({
          variables: {
            input: {
              projectId,
              idempotencyKey: createId(),
              workerId: workerId,
              formName: templateName,
            },
          },
        })
      }
    },
    [
      workerWebProject?.publicProject?.id,
      adminUser?.id,
      createWorkerSignOnFromFormByWorker,
      templateName,
      createWorkerSignOnFromForm,
      projectId,
    ]
  )

  // Track if competent person has already been signed on for this session
  const competentPersonSignedOnRef = React.useRef<string | null>(null)

  const handleCompetentPersonSignatureChange = useCallback(
    async (signature: CompetentPersonSignatureItem) => {
      onChange({
        ...value,
        competentPersonSignature: signature,
      })
      // Only sign on if a new signature is provided and not already signed on
      const currentWorkerId = userWithWorker?.worker?.id || adminUser?.id
      if (
        signature?.url &&
        currentWorkerId &&
        competentPersonSignedOnRef.current !== currentWorkerId
      ) {
        await handleSignOn(currentWorkerId)
        competentPersonSignedOnRef.current = currentWorkerId
      }
    },
    [onChange, value, userWithWorker?.worker?.id, adminUser?.id, handleSignOn]
  )

  const handleWorkersChange = useCallback(
    (workers: WorkerQrCodeSignatureItem[]) => {
      onChange({ ...value, workers })
    },
    [onChange, value]
  )

  return (
    <Flexbox flexDirection="column" gap="2rem">
      <Text>
        You can print off QR code stickers for workers while{' '}
        <Link href={routes.siteInductions.list()} target="_blank">
          reviewing their {t('induction:title').toLowerCase()}
        </Link>{' '}
        or from their{' '}
        <Link href={routes.workers.list()} target="_blank">
          worker page
        </Link>
        .
      </Text>
      <Form.Item name={[id, 'workers']}>
        <SignInSheetOptions
          projectId={projectId}
          value={value?.workers}
          onChange={handleWorkersChange}
          handleSignOn={handleSignOn}
        />
      </Form.Item>

      <Form.Item name={[id, 'competentPersonSignature']}>
        <CompetentPersonsSignature
          projectId={projectId}
          value={value?.competentPersonSignature}
          onChange={handleCompetentPersonSignatureChange}
        />
      </Form.Item>
    </Flexbox>
  )
}

type Props = {
  block: WorkerQrCodeSignatureCaptureBlockType
  rules: Rule[]
}

export const WorkerQrCodeSignatureCaptureBlock: React.FC<Props> = ({
  block,
  rules,
}) => {
  const { projectId, config, extraInfo } = useTemplateRenderer()
  const { workers, competentPersonSignature } = block

  if (config.isPublic) {
    return null
  }

  return (
    <CaptureBlockFormItem
      block={block}
      initialValue={{ workers, competentPersonSignature }}
      rules={rules}
    >
      <SignInSheet
        id={block.id}
        projectId={projectId}
        templateName={extraInfo?.templateName as string}
      />
    </CaptureBlockFormItem>
  )
}
